/**
 * M8 — meet someone. ANONYMOUS register: the @handle IS the name. There is
 * no first-name code path here on purpose — the payload carries `handle`
 * only, and nothing in this file could render anything else.
 *
 * Row or card by density; the card carries the avatar (photo, or the
 * handle's initial), the online mark, and "see their profile" / "someone
 * else". There is no sub-line: the reason chip already says why this person
 * is here and the button says what the tap does, so a third sentence between
 * them was only ever repeating one of the two — and the canvas's version
 * ("they never see your number") described a product with phone numbers in
 * it. The primary tap is `open` — the host's route map opens this person's
 * handle-only public profile (PPL-425);
 * "someone else" is the `rotate_match` verb. W-H2: the online hint lives as
 * this module's sub-line, not its own row.
 */

import { Zap } from 'react-feather';
import { HomeAction, HomeActions, HomeCard, HomeGlyph, HomeRow } from '../HomeKit';
import { atHandle, handleInitial } from '../format';
import type { ModuleProps } from '../moduleTypes';

export function MeetModule({ module, onAction }: ModuleProps) {
  if (module.type !== 'meet') return null;
  const { payload } = module;
  const act = (a: Parameters<NonNullable<typeof onAction>>[0]) => onAction?.(a, module);
  const name = atHandle(payload.handle);

  const seeProfile = () => act({ kind: 'open' });
  const dismiss = module.dismissable ? () => act({ kind: 'dismiss', dismissKey: module.dismissKey }) : undefined;
  const rotate = () => act({ kind: 'rotate_match', matchUserId: payload.matchUserId });

  if (module.density === 'row') {
    return (
      <HomeRow
        reason={module.reason}
        body={name}
        sub={payload.online ? 'online now' : undefined}
        glyph={
          <HomeGlyph tone="magenta">
            <Zap size={13} />
          </HomeGlyph>
        }
        dismissable={module.dismissable}
        onDismiss={dismiss}
        onOpen={seeProfile}
      />
    );
  }

  return (
    <HomeCard
      reason={module.reason}
      body={
        <span className="home-name-row">
          <span className="home-avatar" aria-hidden="true">
            {payload.avatarUrl ? <img src={payload.avatarUrl} alt="" /> : handleInitial(payload.handle)}
          </span>
          {name}
          {payload.online ? <span className="home-online-dot" title="online now" /> : null}
        </span>
      }
      dismissable={module.dismissable}
      onDismiss={dismiss}
      onOpen={seeProfile}
      secondary={
        <HomeActions>
          <HomeAction primary onClick={seeProfile}>see their profile</HomeAction>
          {module.dismissable && <HomeAction onClick={rotate}>someone else</HomeAction>}
        </HomeActions>
      }
    />
  );
}
